import { BrowserRouter, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router';
import './App.css';
import LayoutProvider, { LayoutContext } from './context';
import React, { useContext, useEffect, useState } from 'react';
import { CssBaseline, CssVarsProvider, extendTheme, Box, Typography, Button } from '@mui/joy';
import { AnimatePresence, motion } from 'framer-motion';
import Layout from './pages/Layout';
import TestPage from './pages/TestPage';
import StatsPage from './pages/StatsPage';
import GenerateTestModal from './components/modals/GenerateTestModal';
import TestSessionPage from './pages/test-session/TestSessionPage';
import AdminPage from './pages/AdminPage';
import UsersPage from './pages/UsersPage';
import GeneratorPage from './pages/GeneratorPage';
import TasksPage from './pages/TasksPage';
import ModalAccount from './components/modals/ModalAccount';
import MainPage from './pages/MainPage';
import HomeV2 from './pages/home/HomeV2';

import ModulesPage from './pages/ModulesPage';
import ModuleDetailPage from './pages/ModuleDetailPage';
import PaymentModal from './components/modals/PaymentModal';
import WelcomeModal from './components/modals/WelcomeModal';
import PaidFeedbackModal from './components/modals/PaidFeedbackModal';
import UpdatesPage from './pages/UpdatesPage';
import LeaderboardPage from './pages/LeaderboardPage';
import PlansPage from './pages/PlansPage';
import DocumentsPage from './pages/DocumentsPage';
import FankiLandingPage from './pages/FankiLandingPage';
import GrandSlamLandingPage from './pages/GrandSlamLandingPage';
import ResidencyPage from './pages/ResidencyPage';
import MockExamPage from './pages/mock-exam/MockExamPage';
import MockExamsPage from './pages/mock-exam/MockExamsPage';

// Главная v2 (карта доступа) — по ?home=v2 или localStorage.apex_home='v2'.
// Старая MainPage не меняется, это переключатель на время приёмки.
function HomeSwitch() {
  const [searchParams] = useSearchParams();
  const q = searchParams.get('home');
  let stored = null;
  try { stored = localStorage.getItem('apex_home'); } catch { /* приватный режим */ }
  if (q === 'v2' || q === 'v1') { try { localStorage.setItem('apex_home', q); } catch { /* ignore */ } }
  // v2 по умолчанию с 06.09.2026; ?home=v1 (запоминается) возвращает старую главную
  const v2 = q === 'v2' || (q !== 'v1' && stored !== 'v1');
  return v2 ? <HomeV2 /> : <MainPage />;
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('React crash:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography level="h3" sx={{ mb: 2 }}>Ошибка отображения</Typography>
          <Typography level="body-md" sx={{ mb: 2, color: 'text.secondary' }}>
            {this.state.error?.message || 'Неизвестная ошибка'}
          </Typography>
          <Button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}>
            Перезагрузить
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return children;
};


export const theme = extendTheme({
    colorSchemes: {
      light: {
        palette: {
          primary: {
            50: '#F5F4F0',    // Lightest
            100: '#E9E9E7',   // Very light
            200: '#D4D4D0',   // Light
            300: '#9B9A97',   // Medium
            400: '#5C5B58',   // Darker
            500: '#37352F',   // Main button color (charcoal)
            600: '#2D2C29',   // Hover
            700: '#1F1E1C',   // Active/pressed
            800: '#B84C31',   // Accent color (terracotta)
            900: '#A03D25',   // Accent hover
            // Dark buttons with light text
            solidBg: '#37352F',
            solidHoverBg: '#2D2C29',
            solidActiveBg: '#1F1E1C',
            solidColor: '#FFFFFF',
            // Soft variant - light with dark text
            softBg: '#F5F4F0',
            softHoverBg: '#E9E9E7',
            softActiveBg: '#D4D4D0',
            softColor: '#37352F',
            // Outlined - dark border
            outlinedBorder: '#E9E9E7',
            outlinedHoverBorder: '#D4D4D0',
            outlinedColor: '#37352F',
          },
          danger: {
            50: "#FBE9E9",
            100: "#F8D4D4",
            200: "#F0A8A8",
            300: "#E67D7D",
            400: "#E03E3E",
            500: "#C53030",
            600: "#9B2C2C",
            700: "#822727",
            800: "#63171B",
            900: "#441111"
          },
          // Травяной зелёный (08.09.2026): мятный #C6E7E0 у soft-чипов смотрелся грязно.
          // soft = 100 фон + 700 текст, solid = 500.
          success: {
            50: "#F2F8E9",
            100: "#E4F0D3",
            200: "#C9E3A6",
            300: "#A3CF6E",
            400: "#7CB83A",
            500: "#5F9E1F",
            600: "#4E841A",
            700: "#3E6A15",
            800: "#2F5110",
            900: "#20380B"
          },
          warning: {
            50: "#FEF3C7",
            100: "#FDE68A",
            200: "#FCD34D",
            300: "#FBBF24",
            400: "#F59E0B",
            500: "#D97706",
            600: "#B45309",
            700: "#92400E",
            800: "#78350F",
            900: "#451A03"
          },
          neutral: {
            50: '#FAF9F6',    // Background primary (cream)
            100: '#F5F4F0',   // Background secondary
            200: '#E9E9E7',   // Border default
            300: '#D4D4D0',   // Border hover
            400: '#9B9A97',   // Text muted
            500: '#787774',   // Text secondary
            600: '#5C5B58',   // Text darker
            700: '#37352F',   // Text primary (charcoal)
            800: '#2D2C29',   // Darker
            900: '#1F1E1C',   // Darkest
          },
          background: {
            body: '#FAF9F6',      // Cream background
            surface: '#FFFFFF',   // Cards, surfaces
            popup: '#FFFFFF',     // Modals
            level1: '#F5F4F0',    // Secondary bg
            level2: '#FFFFFF',    // Elevated surfaces
            level3: '#FAF9F6',    // Tertiary bg
          },
          text: {
            primary: '#1A1A1A',   // Near-black for better readability
            secondary: '#5C5B58', // Darker medium gray
            tertiary: '#9B9A97',  // Light gray
          },
          divider: '#E9E9E7',
          focusVisible: '#B84C31',
        },
      },
      dark: {
        palette: {
          primary: {
            50: '#2A2A28',     // Darker
            100: '#3A3A38',    // Dark
            200: '#F5F5F7',    // Button background (light)
            300: '#E8E8EA',    // Button hover
            400: '#DADADC',    // Button active
            500: '#1F1F1F',    // Text on button (dark)
            600: '#0F0F0F',    // Darker text
            700: '#000000',    // Darkest text
            800: '#DA7756',    // Accent color (terracotta)
            900: '#E58A6B',    // Accent hover
            // Light buttons with dark text (for dark mode)
            solidBg: '#F5F5F7',
            solidHoverBg: '#E8E8EA',
            solidActiveBg: '#DADADC',
            solidColor: '#1F1F1F',
            // Soft variant - lighter bg for visibility
            softBg: '#3A3A3A',
            softHoverBg: '#4A4A4A',
            softActiveBg: '#5A5A5A',
            softColor: '#FFFFFF',
            // Outlined - light border
            outlinedBorder: '#5A5A5A',
            outlinedHoverBorder: '#7A7A7A',
            outlinedColor: '#FFFFFF',
          },
          danger: {
            50: "#2A1215",
            100: "#3D1A1D",
            200: "#5C2529",
            300: "#8C3338",
            400: "#CC3340",
            500: "#E64550",
            600: "#F06670",
            700: "#F58890",
            800: "#FAAAB0",
            900: "#FDD4D6"
          },
          success: {
            50: "#15200C",
            100: "#1F3012",
            200: "#2C4419",
            300: "#3B5A21",
            400: "#57822E",
            500: "#7CB83A",
            600: "#98CC5E",
            700: "#B7DD88",
            800: "#D2EBB0",
            900: "#E9F5D6"
          },
          warning: {
            50: "#2D1F0A",
            100: "#4A3310",
            200: "#6B4A18",
            300: "#8C6220",
            400: "#B87A28",
            500: "#D97706",
            600: "#F59E0B",
            700: "#FBBF24",
            800: "#FCD34D",
            900: "#FDE68A"
          },
          neutral: {
            50: '#FFFFFF',    // Body text
            100: '#ECECEC',   // Secondary text
            200: '#C5C5C5',   // Muted text
            300: '#8E8E8E',   // More muted
            400: '#6B6B6B',   // Even more muted
            500: '#3F3F3F',   // Border
            600: '#343434',   // Hover bg, inputs
            700: '#2B2B2B',   // Cards, sidebar
            800: '#1F1F1F',   // Body bg
            900: '#0F0F0F',   // Deepest dark
          },
          background: {
            body: '#1F1F1F',      // Body background
            surface: '#2B2B2B',   // Cards, sidebar
            popup: '#2B2B2B',     // Modals
            level1: '#3A3A3A',    // Hover, inputs (lighter!)
            level2: '#4A4A4A',    // Active elements (lighter!)
            level3: '#1A1A1A',    // Toast, progress
          },
          text: {
            primary: '#FFFFFF',
            secondary: '#C5C5C5',
            tertiary: '#8E8E8E',
          },
          divider: '#3F3F3F',
          focusVisible: '#DA7756',
        },
      }
    },
    radius: {
      xs: '4px',
      sm: '6px',
      md: '8px',
      lg: '12px',
      xl: '16px',
    },
    shadow: {
      xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
      sm: '0 2px 4px rgba(0, 0, 0, 0.06)',
      md: '0 4px 12px rgba(0, 0, 0, 0.08)',
      lg: '0 8px 24px rgba(0, 0, 0, 0.12)',
      xl: '0 16px 48px rgba(0, 0, 0, 0.16)',
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        sm2: 800,
        md: 900,
        lg: 1200,
        xl: 1536,
        extrasmall: 300,
        small: 500,
        medium: 800,
        large: 1100,
      },
    },
    fontFamily: {
      body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      code: '"SF Mono", Monaco, Consolas, "Courier New", monospace',
    },
    fontWeight: {
      sm: 400,
      md: 500,
      lg: 600,
      xl: 700,
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.8125rem',  // 13px
      md: '0.875rem',   // 14px
      lg: '1rem',       // 16px
      xl: '1.125rem',   // 18px
      xl2: '1.375rem',  // 22px
      xl3: '1.75rem',   // 28px
      xl4: '2.25rem',   // 36px
    },
    lineHeight: {
      sm: 1.4,
      md: 1.5,
      lg: 1.7,
    },
    components: {
      JoyButton: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            fontWeight: 600,
            fontSize: '14px',
            transition: 'all 0.15s ease',
            borderRadius: theme.vars.radius.md,
            border: '0.5px solid',
            borderColor: ownerState.variant === 'solid'
              ? 'rgba(0, 0, 0, 0.1)'
              : theme.vars.palette.divider,
            '&:hover': {
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          })
        }
      },
      JoySheet: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.lg,
          })
        }
      },
      JoyCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.lg,
            boxShadow: 'none',
            border: `1px solid ${theme.vars.palette.divider}`,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: theme.vars.palette.neutral[300],
              boxShadow: theme.vars.shadow.md,
            },
          })
        }
      },
      JoyTextarea: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md,
            fontSize: '14px',
            '--Textarea-focusedHighlight': theme.vars.palette.primary[500],
          })
        }
      },
      JoyInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md,
            fontSize: '14px',
            '--Input-focusedHighlight': theme.vars.palette.primary[500],
          })
        }
      },
      JoySelect: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md,
            fontSize: '14px',
          })
        }
      },
      JoyAutocomplete: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md,
          })
        }
      },
      JoyIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md,
            transition: 'all 0.15s ease',
          })
        }
      },
      JoyChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.sm,
            fontWeight: 500,
          })
        }
      },
      JoyAlert: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.lg,
          })
        }
      },
      JoyModal: {
        styleOverrides: {
          root: ({ theme }) => ({
            '--ModalDialog-radius': theme.vars.radius.xl,
          })
        }
      },
      JoyModalDialog: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.xl,
            boxShadow: theme.vars.shadow.xl,
          })
        }
      },
      JoyTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.lg,
          })
        }
      },
      JoyTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.md,
            fontWeight: 500,
            transition: 'all 0.15s ease',
          })
        }
      },
      JoyAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.lg,
          })
        }
      },
      JoyTooltip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.vars.radius.sm,
            fontSize: '13px',
          })
        }
      },
      JoyTypography: {
        styleOverrides: {
          root: {
            lineHeight: 1.6,
          }
        }
      },
    },
});



function AnimatePage() {
  const location = useLocation();
  const { modalAccountOpen, closeModalAccount, paymentModalOpen, setPaymentModalOpen, user } = useContext(LayoutContext);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [paidFeedbackOpen, setPaidFeedbackOpen] = useState(false);

  useEffect(() => {
    // Опрос триггерится на 3+ день использования (не сразу при регистрации).
    // Пропускаем юзеров без survey_completed=true ИЛИ если меньше 3 дней с date_create.
    if (!user || user.survey_completed || !user.date_create) return;
    const daysSinceCreate = (Date.now() - new Date(user.date_create).getTime()) / 86400000;
    if (daysSinceCreate >= 3) {
      setWelcomeOpen(true);
    }
  }, [user]);

  useEffect(() => {
    // Окно сбора отзывов у платных пользователей. Флаг приходит с бэка
    // (активировал платный тариф И ещё не оставил отзыв), снимается после отправки.
    if (user?.paid_feedback_prompt) setPaidFeedbackOpen(true);
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('referral_source', ref);
    }
  }, []);
  // const navigate = useNavigate();
  // const { lock, setLock, account } = useContext(LayoutContext);
  

  return (
    <AnimatePresence mode='wait'>
      <Routes location={location} key={location.pathname}>
        {/* Standalone landings — без Layout и авторизации, ПЕРЕД ProtectedRoute */}
        <Route path="/fanki" element={<FankiLandingPage />} />
        {/* /pro — каноничный URL. /grand-slam и /system оставлены как aliases
            для обратной совместимости (старые ссылки в TG, email-рассылках, etc). */}
        <Route path="/pro" element={<GrandSlamLandingPage />} />
        <Route path="/grand-slam" element={<GrandSlamLandingPage />} />
        <Route path="/system" element={<GrandSlamLandingPage />} />

        <Route path='/' element={
          <ProtectedRoute>
            <Layout/>
          </ProtectedRoute>
        }>
          <Route path="" element={<HomeSwitch />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/plans/:planId" element={<PlansPage />} />
          <Route path="/modules" element={<ModulesPage />} />
          <Route path="/modules/:moduleId" element={<ModuleDetailPage />} />
          <Route path="/residency" element={<ResidencyPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/tests" element={<TestPage />} />
          <Route path="/mock-exams" element={<MockExamsPage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/generator" element={<GeneratorPage />} />
          <Route path="/tasks" element={<ErrorBoundary><TasksPage /></ErrorBoundary>} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/updates" element={<UpdatesPage />} />
        </Route>
        
        {/* Отдельный маршрут для страницы тестирования без основного лейаута */}
        <Route path="/test-session/:testId" element={
          <ProtectedRoute>
            <TestSessionPage />
          </ProtectedRoute>
        } />
        <Route path="/mock-exams/:slug" element={
          <ProtectedRoute>
            <MockExamPage />
          </ProtectedRoute>
        } />

      </Routes>

      <GenerateTestModal />
      <ModalAccount open={modalAccountOpen} onClose={closeModalAccount}/>
      <WelcomeModal open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />
      <PaidFeedbackModal open={paidFeedbackOpen && !welcomeOpen} onClose={() => setPaidFeedbackOpen(false)} />

      {/* Модальные окна платежей */}
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onPaymentSuccess={() => {}}
      />
      
      {/* <PaymentSuccessModal
        open={successPaymentModalOpen}
        onClose={() => setSuccessPaymentModalOpen(false)}
      /> */}
    </AnimatePresence>
  );
}


function App() {
  return (
    <BrowserRouter>
      <LayoutProvider>
        <AnimatePage/>
      </LayoutProvider>
    </BrowserRouter>
  );
}

export default App;
